import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query';
import UpButton from '../../components/Common/UpButton';
import AppPage from './AppPage';
import {
  createSignSession,
  getSignupStatus,
  getSignupErrorMessage,
  getSignupRequestId,
} from '../../api/signup';

// https only — an http iframe inside an https page is silently blocked
// (mixed content) and would render as a blank frame.
function getSafeEmbedUrl(session) {
  try {
    const url = new URL(session?.embed_url);
    return url.protocol === 'https:' ? url.href : null;
  } catch (e) {
    return null;
  }
}

function isPandaDocOrigin(origin) {
  return (
    typeof origin === 'string' &&
    (origin === 'https://app.pandadoc.com' || origin.endsWith('.pandadoc.com'))
  );
}

// Atlance answers a sign-session request for a signed form with 409 "order
// form already signed" — e.g. the window-focus refetch right after the
// merchant signed. That is the success state, never an error.
function isAlreadySigned(err) {
  const detail = err?.response?.data?.detail;
  return (
    err?.response?.status === 409 &&
    typeof detail === 'string' &&
    /already signed/i.test(detail)
  );
}

// The Order Form signs EMBEDDED in this page. The previous new-tab flow
// stranded the merchant on the signing page after signing with no way back
// (Archit's flow review, Aug 6). Atlance's in-house signing page posts no
// message to this window, so "signed" comes from the wizard's 3s status poll
// (or a sign-session 409, above). The PandaDoc completed-event listener is
// left in place but no longer fires (Atlance 410s PandaDoc-era rows). The document spans the
// whole card and can go full screen — a legal document has to be READABLE
// before it's signed.
function SignView({ token, state }) {
  const queryClient = useQueryClient();
  const [embedError, setEmbedError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Reload must remount the iframe even when the server re-uses the same
  // live session (unchanged embedUrl) — the nonce forces a fresh node.
  const [reloadNonce, setReloadNonce] = useState(0);
  // PandaDoc's completed event lands before the server's webhook does — the
  // merchant sees "signed" at once, the page advances when the server agrees.
  const [signedHere, setSignedHere] = useState(false);
  // Reads the wizard's 3s status poll (same key) without polling again.
  const { data: status } = useQuery({
    queryKey: ['signup-status', token],
    queryFn: () => getSignupStatus(token),
    enabled: false,
  });

  // The server re-uses live sessions and re-mints stale ones, so refetching
  // is always safe — refetchOnWindowFocus re-mints for the merchant who
  // opened the link and came back hours after the session expired.
  const {
    data: session,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['signup-sign-session', token],
    queryFn: () => createSignSession(token),
    retry: (failureCount, err) => !isAlreadySigned(err) && failureCount < 1,
    refetchOnWindowFocus: true,
  });
  const alreadySigned = isAlreadySigned(error);
  const signed =
    signedHere || alreadySigned || Boolean(state?.signed || status?.signed);
  const advancing = useIsFetching({ queryKey: ['signup-state', token] }) > 0;

  // The server already has the signature — check status now so the page
  // advances instead of waiting for the next poll.
  useEffect(() => {
    if (alreadySigned) {
      queryClient.invalidateQueries({ queryKey: ['signup-status', token] });
    }
  }, [alreadySigned, queryClient, token]);

  useEffect(() => {
    function onMessage(event) {
      if (!isPandaDocOrigin(event.origin)) {
        return;
      }

      if (event.data?.type === 'session_view.document.completed') {
        // Signed — leave full screen so the merchant sees the page advance,
        // and make the status poll fire now instead of in ≤3s.
        setExpanded(false);
        setSignedHere(true);
        queryClient.invalidateQueries({ queryKey: ['signup-status', token] });
      }

      if (event.data?.type === 'session_view.document.exception') {
        setExpanded(false);
        setEmbedError(true);
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [queryClient, token]);

  // Best-effort: Escape only reaches this window while focus is OUTSIDE the
  // cross-origin iframe, so the button never advertises it (CodeRabbit) —
  // the visible exit is the button itself.
  useEffect(() => {
    if (!expanded) {
      return undefined;
    }

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        setExpanded(false);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [expanded]);

  // The status poll advances the page on its own; this is the manual nudge
  // for a merchant who'd rather not wait for it.
  function continueAfterSigning() {
    queryClient.invalidateQueries({ queryKey: ['signup-status', token] });
    queryClient.invalidateQueries({ queryKey: ['signup-state', token] });
  }

  function reloadSigningForm() {
    setEmbedError(false);
    setReloadNonce(value => value + 1);
    refetch();
  }

  const embedUrl = getSafeEmbedUrl(session);
  const showBadSession = !error && session && !embedUrl;
  const showError =
    !signed && ((error && !alreadySigned) || showBadSession || embedError);

  const documentArea = (
    <>
      {embedUrl && !embedError && (
        <div className={cx('signup-sign-frame-wrap mt-6', { expanded })}>
          <div className="sign-frame-bar">
            <span className="frame-bar-title">
              Order Form — {state?.business || 'your brand'}
            </span>
            <button type="button" onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Exit full screen' : '⛶ Read full screen'}
            </button>
          </div>
          {/* sandbox: signing needs scripts+forms+same-origin (PandaDoc's
              documented embed set); popups because the Order Form carries
              clickable ToS links that open in a new tab, and the post-sign
              screen offers the signed-copy download. Top-level navigation
              stays blocked — that's the hijack vector. */}
          <iframe
            key={`${embedUrl}-${reloadNonce}`}
            title="UrbanPiper — Order Form"
            src={embedUrl}
            className="signup-sign-frame"
            allow="clipboard-write"
            sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads"
          />
        </div>
      )}
      {/* Signed: the "review and sign" hint and its reload link go away —
          also when the signed state came from a 409 with no frame on screen. */}
      {signed && (
        <div className="mt-5 flex flex-col items-center gap-3">
          <p className="text-sm text-up-clr-dark-grey text-center">
            Order form signed — taking you to the next step…
          </p>
          <UpButton
            wrapperClass="w-max"
            disabled={advancing}
            onClick={continueAfterSigning}>
            {advancing ? 'Checking…' : 'Continue'}
          </UpButton>
        </div>
      )}
      {!signed && embedUrl && !embedError && (
        <p className="text-xs text-up-clr-light-grey text-center mt-3">
          Review and sign above — this page moves ahead automatically once
          you&rsquo;ve signed.{' '}
          <button
            type="button"
            className="underline"
            onClick={reloadSigningForm}>
            Reload the form
          </button>{' '}
          if it looks stuck.
        </p>
      )}
    </>
  );

  return (
    <AppPage step={state?.step}
      noPayment={state?.payment_at_signup === 'invoice'} loading={isLoading}
      fullWidth={documentArea}>
      <h1 className="signup-title">Sign your order form</h1>
      <p className="signup-sub">
        {state?.business || 'Your brand'}&apos;s Order Form is ready for
        signature.
      </p>
      {/* View-only from here — the details are on the legal document
          (owner's rule: see, don't edit, once the form exists). */}
      <details className="text-sm text-up-clr-dark-grey">
        <summary className="cursor-pointer text-up-clr-light-grey">
          Your details (view only)
        </summary>
        <div className="mt-2">
          <div>Brand: {state?.business}</div>
          <div>Legal entity: {state?.identity?.legal_entity}</div>
          <div>
            Billing contact: {state?.identity?.billing_contact_name} ·{' '}
            {state?.identity?.billing_contact_email}
          </div>
          {state?.billing_address && (
            <div>
              {/* no state outside the US (e.g. Austria) — skip empty parts */}
              Address:{' '}
              {[
                state.billing_address.street,
                state.billing_address.city,
                [state.billing_address.state, state.billing_address.zip]
                  .filter(Boolean)
                  .join(' '),
                state?.country && state.country !== 'United States'
                  ? state.country
                  : '',
              ]
                .filter(Boolean)
                .join(', ')}
            </div>
          )}
          {state?.vat_id && <div>VAT ID: {state.vat_id}</div>}
        </div>
      </details>
      {showError && (
        <div className="text-sm text-up-clr-semantic-red break-words mt-6">
          {error
            ? getSignupErrorMessage(
                error,
                'We could not prepare your signing page. Please try again.',
              )
            : 'The signing form ran into a problem. Please reload it.'}
          {getSignupRequestId(error) && (
            <span className="block text-xs text-up-clr-light-grey mt-1">
              Reference: {getSignupRequestId(error)}
            </span>
          )}
          <UpButton
            variant="link"
            wrapperClass="w-max mt-2"
            onClick={reloadSigningForm}>
            Reload signing form
          </UpButton>
        </div>
      )}
    </AppPage>
  );
}

export default SignView;
